import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  filter,
  forkJoin,
  map,
  merge,
  Observable,
  race,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { Option } from '@sapphire/result';

import { ToastService } from '@ps/core-overlay';
import {
  AdvancedQualification,
  DeprecatedQualificationRecord,
  mapSurveyLocalizationFor,
  prepareDeprecatedQualificationRecords,
  Qualification,
  Survey,
  SURVEY_WORKFLOW_PAGE,
} from '@ps/survey-utils';

import {
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from '../core/survey-creation-core.actions';
import {
  selectCoreChildren,
  selectSurveyWorkflowPage,
} from '../core/survey-creation-core.selectors';
import { SurveyCreationReferenceApiActions } from '../reference/survey-creation-reference.actions';
import {
  selectQualificationsRecord,
  selectQuestionsRecord,
} from '../reference/survey-creation-reference.selectors';
import { surveyAdvancedQualificationCacheKey } from '../../utils/cache/survey-qualification-cache.helper';
import {
  deprecatedQualificationWarnings,
  rootLoadSurveys,
  surveysByLocale,
} from './utils/deprecated-qualifications-on-load.helper';

// Warns about deprecated qualifications the loaded surveys apply, on the audience page.
@Injectable()
export class SurveyCreationDeprecatedQualificationsOnLoadEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _toast = inject(ToastService);

  // Every record's locale qualifications load, so all are awaited together.
  public readonly warnDeprecatedQualificationsOnLoad$ = createEffect(
    () =>
      this._loadedSurveys().pipe(
        switchMap((surveys) =>
          forkJoin(
            surveys.map((survey) =>
              this._qualificationsFor(survey).pipe(
                map((qualifications) =>
                  qualifications.mapOr([], (loaded) =>
                    prepareDeprecatedQualificationRecords(
                      survey.qualifications ?? [],
                      loaded,
                      [],
                    ),
                  ),
                ),
              ),
            ),
          ).pipe(
            map((deprecated) => ({ surveys, deprecated: deprecated.flat() })),
          ),
        ),
        tap(({ surveys, deprecated }) => this._warn(surveys, deprecated)),
      ),
    { dispatch: false },
  );

  // Advanced qualifications may load for some locales only, so each locale warns on its own.
  public readonly warnDeprecatedAdvancedQualificationsOnLoad$ = createEffect(
    () =>
      this._loadedSurveys().pipe(
        switchMap((surveys) =>
          merge(
            ...[...surveysByLocale(surveys)].map(([locale, localeSurveys]) =>
              this._advancedQualificationsFor(locale).pipe(
                map((advancedQualifications) => ({
                  surveys,
                  deprecated: advancedQualifications.mapOr([], (loaded) =>
                    localeSurveys.flatMap((survey) =>
                      prepareDeprecatedQualificationRecords(
                        survey.qualifications ?? [],
                        [],
                        loaded,
                      ),
                    ),
                  ),
                })),
              ),
            ),
          ),
        ),
        tap(({ surveys, deprecated }) => this._warn(surveys, deprecated)),
      ),
    { dispatch: false },
  );

  // Edit page only.
  private _loadedSurveys(): Observable<readonly Survey[]> {
    return merge(
      this._surveysOnRootLoad(),
      this._actions$.pipe(
        ofType(SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess),
        map(({ surveys }) => surveys),
      ),
    ).pipe(
      concatLatestFrom(() => this._store.select(selectSurveyWorkflowPage)),
      filter(([, page]) =>
        page.mapOr(false, (current) => current === SURVEY_WORKFLOW_PAGE.EDIT),
      ),
      map(([surveys]) => surveys),
    );
  }

  // Every page refetches its root; a family's first load is left to loadChildSurveysSuccess.
  private _surveysOnRootLoad(): Observable<readonly Survey[]> {
    return this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess),
      concatLatestFrom(() => this._store.select(selectCoreChildren)),
      map(([{ survey }, children]) => rootLoadSurveys(survey, children)),
      filter((surveys) => surveys.isSome()),
      map((surveys) => surveys.unwrap()),
    );
  }

  // None on a failed load, so the record is skipped.
  private _qualificationsFor(
    survey: Survey,
  ): Observable<Option<readonly Qualification[]>> {
    const locale = mapSurveyLocalizationFor(survey);
    return race(
      this._store.select(selectQualificationsRecord).pipe(
        map((record) => Option.from(record[locale]?.data)),
        filter((qualifications) => qualifications.isSome()),
        take(1),
      ),
      this._actions$.pipe(
        ofType(SurveyCreationReferenceApiActions.loadQualificationsFailure),
        filter((failure) => mapSurveyLocalizationFor(failure) === locale),
        take(1),
        map(() => Option.none),
      ),
    );
  }

  // None on a failed load; never emits if never requested.
  private _advancedQualificationsFor(
    locale: string,
  ): Observable<Option<readonly AdvancedQualification[]>> {
    const key = surveyAdvancedQualificationCacheKey('', [], locale);
    return race(
      this._store.select(selectQuestionsRecord).pipe(
        map((record) => Option.from(record[key]?.data)),
        filter((advancedQualifications) => advancedQualifications.isSome()),
        take(1),
      ),
      this._actions$.pipe(
        ofType(SurveyCreationReferenceApiActions.loadQuestionsFailure),
        filter((failure) => failure.key === key),
        take(1),
        map(() => Option.none),
      ),
    );
  }

  private _warn(
    surveys: readonly Survey[],
    deprecated: readonly DeprecatedQualificationRecord[],
  ): void {
    for (const message of deprecatedQualificationWarnings(
      surveys,
      deprecated,
    )) {
      this._toast.warning(message);
    }
  }
}
